import React from "react";
import { Modal, Input, Icon } from "antd";
import PropTypes from "prop-types";
import { getCountryFlag } from "../utils/formatters";

/**
 * TransferPackageModal - Modal for transferring wallet to different package
 */
const TransferPackageModal = ({
  visible,
  record,
  packages,
  packagesLoading,
  selectedPackageId,
  currentPackageInfo,
  note,
  loading,
  onPackageChange,
  onNoteChange,
  onCancel,
  onSubmit,
}) => {
  if (!record) return null;

  const selectedPkg = packages.find((p) => p.ID === selectedPackageId);
  const currentCredit = record.credit_available;
  const currentPrice = currentPackageInfo ? currentPackageInfo.Amount : null;

  return (
    <Modal
      title={
        <span>
          <Icon type="swap" style={{ marginRight: 8 }} />
          Transfer Package
        </span>
      }
      visible={visible}
      onCancel={onCancel}
      onOk={onSubmit}
      confirmLoading={loading}
      okText="Confirm Transfer"
      okButtonProps={{ disabled: !selectedPackageId || !note || note.trim() === "" }}
      width={480}
      maskClosable={false}
    >
      {packagesLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Icon type="loading" style={{ fontSize: 24 }} />
          <div style={{ marginTop: 8, color: "#666" }}>Loading packages...</div>
        </div>
      ) : (
        <React.Fragment>
          {/* Current Package Card */}
          <div
            style={{
              marginBottom: 16,
              padding: 16,
              background: "#f8f9fa",
              borderRadius: 8,
              border: "1px solid #e9ecef",
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: "#666",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Current Package
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 28 }}>{getCountryFlag(record.country)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
                  {record.package_name || "N/A"}
                </div>
                <div style={{ fontSize: 13, color: "#666" }}>
                  {record.country} • {currentCredit.toLocaleString()} credits
                  {currentPrice && ` • $${currentPrice}`}
                </div>
              </div>
            </div>
          </div>

          {/* Arrow Indicator */}
          <div style={{ textAlign: "center", margin: "12px 0" }}>
            <Icon type="arrow-down" style={{ fontSize: 18, color: "#1890ff" }} />
          </div>

          {/* Select New Package */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 12,
                color: "#666",
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Select New Package
            </div>
            <select
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 14,
                borderRadius: 6,
                border: "1px solid #d9d9d9",
                background: "#fff",
                cursor: "pointer",
              }}
              value={selectedPackageId || ""}
              onChange={(e) => onPackageChange(parseInt(e.target.value, 10) || null)}
            >
              <option value="">-- Choose a package --</option>
              {packages.map((pkg) => (
                <option key={pkg.ID} value={pkg.ID}>
                  {getCountryFlag(pkg.CountryAbbr)} {pkg.Name} - {pkg.CountryAbbr} ($
                  {pkg.Amount} • {pkg.Quantity} credits)
                </option>
              ))}
            </select>
            {packages.length === 0 && (
              <div
                style={{
                  color: "#ff4d4f",
                  fontSize: 12,
                  marginTop: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon type="exclamation-circle" style={{ marginRight: 4 }} />
                No packages available with the same price
              </div>
            )}
          </div>

          {/* New Package Details Card */}
          {selectedPkg && (
            <div
              style={{
                marginBottom: 16,
                padding: 16,
                background: "linear-gradient(135deg, #1890ff 0%, #0050b3 100%)",
                borderRadius: 8,
                color: "#fff",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  marginBottom: 8,
                  opacity: 0.85,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                New Package
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 32 }}>
                  {getCountryFlag(selectedPkg.CountryAbbr)}
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {selectedPkg.Name}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.9 }}>
                    {selectedPkg.CountryAbbr}
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px 16px",
                  fontSize: 13,
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: 6,
                  padding: 10,
                }}
              >
                <div>
                  <strong>Price:</strong> ${selectedPkg.Amount}
                </div>
                <div>
                  <strong>Credits:</strong> {selectedPkg.Quantity.toLocaleString()}
                </div>
                <div>
                  <strong>Unit:</strong> ${selectedPkg.UnitPrice}/credit
                </div>
                <div>
                  <strong>Expiry:</strong> {selectedPkg.Expire} months
                </div>
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <div
              style={{
                fontSize: 12,
                color: "#666",
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Note <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <Input.TextArea
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Reason for transfer..."
              rows={2}
              style={{ borderRadius: 6 }}
            />
          </div>
        </React.Fragment>
      )}
    </Modal>
  );
};

TransferPackageModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  record: PropTypes.object,
  packages: PropTypes.array,
  packagesLoading: PropTypes.bool,
  selectedPackageId: PropTypes.number,
  currentPackageInfo: PropTypes.object,
  note: PropTypes.string,
  loading: PropTypes.bool,
  onPackageChange: PropTypes.func.isRequired,
  onNoteChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

TransferPackageModal.defaultProps = {
  record: null,
  packages: [],
  packagesLoading: false,
  selectedPackageId: null,
  currentPackageInfo: null,
  note: "",
  loading: false,
};

export default TransferPackageModal;



// WEBPACK FOOTER //
// ./src/containers/User/UserManagement/components/MemberCreditWallet/modals/TransferPackageModal.js